/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Callout, RadioButtonGroup, RadioButton, Chip } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { type RunRunbookActionResource, type EnvironmentResource, type RunbookResource, type ProjectResource, type TagSetResource, HasGitPersistenceSettings, HasRunbooksInGit, toGitBranch } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { repository } from "~/clientInstance";
import { ChipIcon, EnvironmentChip } from "~/components/Chips";
import LookupResourceChipComponent from "~/components/LookupResourceChip/LookupResourceChip";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { RunbookTagMultiSelect } from "~/components/MultiSelect/RunbookTagMultiSelect";
import Tag from "~/components/Tag";
import { ExpandableFormSection, Summary } from "~/components/form";
import { getAllByScopes } from "~/components/tagSets";
import { hydrateTagsByName } from "~/components/tagSummary";
import ScheduledTriggerDescriptionHelper from "~/utils/ScheduledTriggerDescriptionHelper/ScheduledTriggerDescriptionHelper";
interface DeployNewReleaseActionEditorProps {
    project: ProjectResource;
    runbooks: RunbookResource[];
    environments: EnvironmentResource[];
    action: RunRunbookActionResource;
    onActionChange(action: RunRunbookActionResource): void;
}
type SelectionMode = "runbook" | "tags";
interface DeployNewReleaseActionEditorState {
    selectedEnvironments: string[];
    selectedRunbookId?: string;
    selectedRunbookTags: string[];
    selectionMode: SelectionMode;
    environments: EnvironmentResource[];
    tagSets?: TagSetResource[];
    isSwitchingMode: boolean;
    runbookSectionExpanded: boolean;
    tagsSectionExpanded: boolean;
}
export class RunRunbookActionEditor extends React.Component<DeployNewReleaseActionEditorProps, DeployNewReleaseActionEditorState> {
    constructor(props: DeployNewReleaseActionEditorProps) {
        super(props);
        this.state = {
            environments: this.props.environments,
            ...this.initState(this.props.action),
        };
    }
    async componentDidMount() {
        const loadedTagSets = await getAllByScopes(["Runbook"]);
        this.setState({
            tagSets: loadedTagSets,
        });
    }
    componentDidUpdate(prevProps: DeployNewReleaseActionEditorProps) {
        if (this.props.action.RunbookTags !== prevProps.action.RunbookTags) {
            const hasRunbookTags = !!this.props.action.RunbookTags && this.props.action.RunbookTags.length > 0;
            if (hasRunbookTags) {
                if (JSON.stringify(this.props.action.RunbookTags) !== JSON.stringify(this.state.selectedRunbookTags)) {
                    this.setState({ selectedRunbookTags: [...this.props.action.RunbookTags!] });
                }
            }
        }
    }
    //eslint-disable-next-line react/no-unsafe
    UNSAFE_componentWillReceiveProps(newProps: DeployNewReleaseActionEditorProps) {
        if (this.state.isSwitchingMode) {
            this.setState({ isSwitchingMode: false });
            return;
        }
        const runbookIdChanged = newProps.action.RunbookId !== this.props.action.RunbookId;
        const runbookTagsChanged = JSON.stringify(newProps.action.RunbookTags) !== JSON.stringify(this.props.action.RunbookTags);
        const environmentsChanged = this.checkEnvironmentIdsChanged(newProps);
        if (runbookIdChanged || runbookTagsChanged || environmentsChanged) {
            this.setState(this.initState(newProps.action, false));
        }
    }
    render() {
        const selectedRunbook = this.getSelectedRunbook(this.state.selectedRunbookId);
        const hasGitRunbooks = HasGitPersistenceSettings(this.props.project.PersistenceSettings) && HasRunbooksInGit(this.props.project.PersistenceSettings);
        return (<div>
                <ExpandableFormSection errorKey="RunbookSelection" title="Runbook selection" focusOnExpandAll summary={this.buildSelectionModeSummary()} help="Choose how to select which runbook(s) to run.">
                    <RadioButtonGroup value={this.state.selectionMode} onChange={this.onSelectionModeChange} accessibleName="Runbook selection mode">
                        <RadioButton value="runbook" label="Select specific runbook" accessibleName="Select specific runbook by name"/>
                        <RadioButton value="tags" label="Select by tags" accessibleName="Select runbooks by tags"/>
                    </RadioButtonGroup>
                </ExpandableFormSection>

                {this.state.selectionMode === "runbook" && (<ExpandableFormSection errorKey="RunbookToRun" title="Runbook" focusOnExpandAll summary={this.buildRunbookSummary()} help="The Runbook to run on selected environments." isExpandedByDefault={this.state.runbookSectionExpanded}>
                        <DeprecatedSelect items={this.props.runbooks.map((e) => {
                    return { value: e.Id, text: e.Name };
                })} placeholder="Select a runbook" allowClear={false} value={this.state.selectedRunbookId} onChange={this.onRunbookSelected}/>
                        {hasGitRunbooks ? (<Callout type={"information"} title="Runbooks in Git">
                                Triggers for Runbooks will always run the latest version on the default branch.
                            </Callout>) : (<>
                                {selectedRunbook && !selectedRunbook.PublishedRunbookSnapshotId && (<Callout type={"warning"} title="This runbook has not been published">
                                        Triggers for Runbooks will only run when a published snapshot is available.
                                    </Callout>)}
                                {selectedRunbook && selectedRunbook.PublishedRunbookSnapshotId && (<Callout type={"information"} title="Trigger will use published snapshot">
                                        Triggers will always run the published snapshot of the runbook.
                                    </Callout>)}
                            </>)}
                    </ExpandableFormSection>)}

                {this.state.selectionMode === "tags" && (<ExpandableFormSection errorKey="RunbookTags" title="Runbook tags" focusOnExpandAll summary={this.buildTagsSummary()} help="Select tags to filter runbooks. All runbooks matching any of these tags will be executed when the trigger fires." isExpandedByDefault={this.state.tagsSectionExpanded}>
                        <RunbookTagMultiSelect value={this.state.selectedRunbookTags} onChange={this.onRunbookTagsChange} doBusyTask={async (task) => {
                    await task();
                    return true;
                }} items={this.state.tagSets}/>
                        <Callout type={"information"} title="Dynamic runbook selection">
                            Tags are evaluated at execution time. The runbooks matching these tags may change as tags are added or removed from runbooks.
                        </Callout>
                        {hasGitRunbooks && (<Callout type={"information"} title="Runbooks in Git">
                                Triggers will run the latest version of all matching runbooks on the default branch.
                            </Callout>)}
                    </ExpandableFormSection>)}

                <ExpandableFormSection errorKey="TargetEnvironments" title="Target environments" focusOnExpandAll summary={this.buildTargetEnvironmentSummary()} help="The environments to run the selected runbook(s).">
                    <EnvironmentMultiSelect environments={this.state.environments} onChange={this.onChangeEnvironments} value={this.state.selectedEnvironments}/>
                </ExpandableFormSection>
            </div>);
    }
    private onRunbookTagsChange = (canonicalTagNames: string[]) => {
        this.setState({
            selectedRunbookTags: canonicalTagNames,
            isSwitchingMode: true,
        }, () => this.raiseChange());
    };
    private onSelectionModeChange = (value: string) => {
        if (value !== "runbook" && value !== "tags") {
            return;
        }
        const newMode: SelectionMode = value;
        this.setState((prevState) => ({
            selectionMode: newMode,
            selectedRunbookId: prevState.selectedRunbookId,
            selectedRunbookTags: prevState.selectedRunbookTags,
            selectedEnvironments: prevState.selectedEnvironments,
            isSwitchingMode: true,
            runbookSectionExpanded: newMode === "runbook" ? true : prevState.runbookSectionExpanded,
            tagsSectionExpanded: newMode === "tags" ? true : prevState.tagsSectionExpanded,
        }), () => {
            this.raiseChange();
        });
    };
    private getSelectedRunbook = (runbookId?: string) => {
        if (!runbookId)
            return null;
        const filteredRunbooks = this.props.runbooks.filter((runbook) => runbook.Id === runbookId);
        return filteredRunbooks.length > 0 ? filteredRunbooks[0] : null;
    };
    private checkEnvironmentIdsChanged = (newProps: DeployNewReleaseActionEditorProps) => {
        if (newProps.action && this.props.action && newProps.action.EnvironmentIds && this.props.action.EnvironmentIds && newProps.action.EnvironmentIds.length !== this.props.action.EnvironmentIds.length) {
            return true;
        }
        else if (newProps.action && newProps.action.EnvironmentIds) {
            newProps.action.EnvironmentIds.forEach((environmentId) => {
                if (!this.props.action.EnvironmentIds || !this.props.action.EnvironmentIds.includes(environmentId)) {
                    return true;
                }
            });
        }
        return false;
    };
    private initState = (action: RunRunbookActionResource, preserveMode: boolean = false) => {
        const hasRunbookTags = !!action.RunbookTags && action.RunbookTags.length > 0;
        const selectionMode: SelectionMode = preserveMode && this.state?.selectionMode ? this.state.selectionMode : hasRunbookTags ? "tags" : "runbook";
        return {
            selectedEnvironments: [...(action.EnvironmentIds ? action.EnvironmentIds : [])],
            selectedRunbookId: preserveMode && this.state?.selectedRunbookId ? this.state.selectedRunbookId : action.RunbookId,
            selectedRunbookTags: preserveMode && this.state?.selectedRunbookTags && this.state.selectedRunbookTags.length > 0 ? this.state.selectedRunbookTags : hasRunbookTags ? [...action.RunbookTags!] : [],
            selectionMode,
            isSwitchingMode: false,
            runbookSectionExpanded: preserveMode && this.state?.runbookSectionExpanded !== undefined ? this.state.runbookSectionExpanded : false,
            tagsSectionExpanded: preserveMode && this.state?.tagsSectionExpanded !== undefined ? this.state.tagsSectionExpanded : false,
        };
    };
    private onChangeEnvironments = (selectedEnvironments: string[]) => {
        this.setState({
            selectedEnvironments,
            isSwitchingMode: true,
        }, () => this.raiseChange());
    };
    private onRunbookSelected = async (selectedRunbookId: string | undefined) => {
        const runbook = this.getSelectedRunbook(selectedRunbookId!);
        if (runbook) {
            const environments = await this.getRunbookEnvironments(this.props.project, runbook);
            const validEnvironmentIds = environments.map((env) => env.Id);
            this.setState((prevState) => ({
                selectedRunbookId: selectedRunbookId!,
                environments,
                selectedEnvironments: prevState.selectedEnvironments.filter((envId) => validEnvironmentIds.includes(envId)),
                isSwitchingMode: true,
            }), () => this.raiseChange());
        }
    };
    private getRunbookEnvironments = async (project: ProjectResource, runbook: RunbookResource) => {
        if (HasGitPersistenceSettings(project.PersistenceSettings) && HasRunbooksInGit(project.PersistenceSettings)) {
            const defaultBranch = toGitBranch(project.PersistenceSettings.DefaultBranch);
            return await repository.Runbooks.getRunbookEnvironments(project, runbook.Id, defaultBranch);
        }
        else {
            return await repository.Runbooks.getRunbookEnvironments(project, runbook.Id);
        }
    };
    private raiseChange = () => {
        const action: RunRunbookActionResource = {
            ...this.props.action,
            EnvironmentIds: this.state.selectedEnvironments,
        };
        if (this.state.selectionMode === "runbook") {
            action.RunbookId = this.state.selectedRunbookId;
            action.RunbookTags = undefined;
        }
        else {
            action.RunbookId = undefined;
            action.RunbookTags = this.state.selectedRunbookTags.length > 0 ? this.state.selectedRunbookTags : undefined;
        }
        this.props.onActionChange(action);
    };
    private buildSelectionModeSummary = () => {
        if (this.state.selectionMode === "runbook") {
            return Summary.summary(<span>Select specific runbook</span>);
        }
        else {
            return Summary.summary(<span>Select by tags</span>);
        }
    };
    private buildRunbookSummary = () => {
        return this.state.selectedRunbookId ? Summary.summary(<span>{ScheduledTriggerDescriptionHelper.lookupRunbookChip(this.state.selectedRunbookId, this.props.runbooks)}</span>) : Summary.placeholder("No runbook selected");
    };
    private buildTagsSummary = () => {
        if (!this.state.selectedRunbookTags || this.state.selectedRunbookTags.length === 0) {
            return Summary.placeholder("No tags selected");
        }
        if (!this.state.tagSets) {
            return Summary.summary(<span>
                    Run all runbooks matching:{" "}
                    {this.state.selectedRunbookTags.map((canonicalName, index) => (<Chip key={index} label={canonicalName}/>))}
                </span>);
        }
        const hydratedTags = hydrateTagsByName(this.state.tagSets, this.state.selectedRunbookTags).filter((item) => item !== null);
        return Summary.summary(<span>
                Run all runbooks matching:{" "}
                {hydratedTags.map((hydratedTag) => (<Tag key={hydratedTag.tag.Id} tagName={hydratedTag.tag.Name} tagColor={hydratedTag.tag.Color} description={hydratedTag.tag.Description}/>))}
            </span>);
    };
    private buildTargetEnvironmentSummary = () => {
        const runbookText = this.state.selectionMode === "tags" ? "Matching runbooks" : "Selected runbook";
        return this.state.selectedEnvironments && this.state.selectedEnvironments.length > 0
            ? Summary.summary(<span>
                      {runbookText} will be run on {this.state.selectedEnvironments.map((selectedEnvironment) => this.environmentChip(selectedEnvironment))} environment{this.state.selectedEnvironments.length > 1 ? "s" : ""}
                  </span>)
            : Summary.placeholder("No target environment selected");
    };
    private environmentChip = (id: string) => {
        const LookupEnvironmentChip = LookupResourceChipComponent<EnvironmentResource>();
        return <LookupEnvironmentChip lookupCollection={this.props.environments} key={id} lookupId={id} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>;
    };
    static displayName = "RunRunbookActionEditor";
}
